import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Minus, Plus, Search, Trash2, Archive, Package, Printer, CheckCircle2, Scale, X } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";
import { useAllocation, AllocationSummary, GrowingFolders, AllocateModal } from "./allocation/AllocationPanel";
import { AuditSnapshots } from "./allocation/AuditSnapshots";

/* Audit Status — nothing here changes stock on its own. Kayla presses the
   buttons when she's ready: mark the count complete, reconcile the allocation
   ledger, or run custom orders against the finished audit. */
function AuditStatusPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [report, setReport] = useState(null);

  const load = () => api.get("/admin/audit-status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setComplete = async (complete) => {
    setBusy("status");
    try {
      const r = await api.post("/admin/audit-status", { status: complete ? "complete" : "in_progress" });
      setStatus(r.data);
      toast.success(complete ? "Audit marked complete." : "Audit reopened — back to In Progress.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update the audit status."); }
    finally { setBusy(""); }
  };

  const reconcile = async () => {
    setBusy("reconcile");
    try {
      const r = await api.post("/admin/audit-reconcile", {});
      setReport({ title: "Stock reconciliation", ...r.data });
      toast.success(`Reconciled — ${r.data.released_allocations} stale allocation${r.data.released_allocations === 1 ? "" : "s"} released.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reconcile stock."); }
    finally { setBusy(""); }
  };

  if (!status) return null;
  const complete = status.status === "complete";

  return (
    <section className="bg-white border border-[#E8DFCB] rounded p-4 space-y-3" data-testid="audit-status-panel">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">Audit status</div>
          <div className="font-serif text-2xl" data-testid="audit-status-value" style={{ color: complete ? "#2F6B4F" : "#8A5D12" }}>
            {complete ? "Complete" : "In Progress"}
          </div>
        </div>
        <div className="text-xs text-[#7A6A55] space-y-0.5">
          <div data-testid="audit-status-allocated">Allocated: <strong>{status.allocated_tubers}</strong> · Available: <strong>{status.available_tubers}</strong> of {status.total_tubers} counted</div>
          <div>Marked complete: {status.marked_complete_at ? new Date(status.marked_complete_at).toLocaleString("en-NZ") : "not yet"}</div>
          <div>Last reconciliation: {status.last_reconciliation_at ? new Date(status.last_reconciliation_at).toLocaleString("en-NZ") : "never"}</div>
          <div>Last order allocation: {status.last_custom_allocation_at ? new Date(status.last_custom_allocation_at).toLocaleString("en-NZ") : "never"}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setComplete(!complete)} disabled={busy === "status"} data-testid="audit-mark-complete"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white disabled:opacity-40"
          style={{ backgroundColor: complete ? "#8A5D12" : "#2F6B4F" }}>
          <CheckCircle2 className="w-4 h-4" /> {complete ? "Reopen audit" : "Mark audit complete"}
        </button>
        <button type="button" onClick={reconcile} disabled={busy === "reconcile"} data-testid="audit-reconcile"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-[#3E1F46] text-[#3E1F46] bg-white text-[11px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
          {busy === "reconcile" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />} Run stock reconciliation
        </button>
      </div>
      <p className="text-[11px] italic text-[#9C8B73]">Nothing runs automatically. Allocate stock to customer orders from the Pull List; allocate to growing folders from the list below.</p>
      {report && (
        <div className="border-l-4 border-[#C49247] border border-[#E8DFCB] rounded p-3 text-xs" data-testid="audit-report">
          <div className="flex items-start justify-between gap-3">
            <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#8A5D12]">{report.title}</div>
            <button type="button" onClick={() => setReport(null)} aria-label="Close" data-testid="audit-report-close"><X className="w-4 h-4 text-[#9C8B73]" /></button>
          </div>
          {report.released_allocations !== undefined && (
            <div className="mt-1">Released stale allocations: <strong>{report.released_allocations}</strong> · currently allocated <strong>{report.allocated_total}</strong></div>
          )}
          {(report.over_allocated || []).length > 0 && (
            <div className="mt-1 text-[#A33030]">Over-allocated: {report.over_allocated.map((o) => `${o.name} (+${o.over_by})`).join(", ")}</div>
          )}
          {(report.not_in_audit || []).length > 0 && (
            <div className="mt-1 text-[#8A5D12]">Allocated but not on the audit: {report.not_in_audit.map((o) => o.name).join(", ")}</div>
          )}
          {(report.allocated || []).length > 0 && (
            <div className="mt-1 text-[#2F6B4F]">Allocated now: {report.allocated.map((a) => `${a.qty}× ${a.name} → #${a.order_number}`).join(", ")}</div>
          )}
          {(report.shortages || []).length > 0 && (
            <div className="mt-1 text-[#A33030]">Shortages: {report.shortages.map((s) => `#${s.order_number} ${s.name} short ${s.shortage}`).join(", ")} — resolve these on the Pull List.</div>
          )}
          {report.shortages && report.shortages.length === 0 && report.allocated && (
            <div className="mt-1 text-[#2F6B4F]">No shortages — everything requested is covered.</div>
          )}
        </div>
      )}
    </section>
  );
}

/* Tuber Audit — Kayla's master tuber stock list. Search the catalogue or
   type a new name, record counted tubers, edit inline, start fresh audits
   (archived, never deleted). Manual by design — no auto subtraction. */

export default function AdminTuberAudit() {
  const { board, reload: reloadBoard } = useAllocation();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_varieties: 0, total_tubers: 0 });
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [allocating, setAllocating] = useState(null);
  const nameRef = useRef(null);
  const boardByKey = useMemo(() => {
    const m = {};
    for (const row of board?.rows || []) m[row.variety_key] = row;
    return m;
  }, [board]);

  const load = async () => {
    try {
      const r = await api.get("/admin/tuber-audit");
      setRows(r.data?.rows || []);
      setTotals({ total_varieties: r.data?.total_varieties || 0, total_tubers: r.data?.total_tubers || 0 });
    } catch { toast.error("Couldn't load the tuber audit."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/admin/catalog").then((r) => setCatalog(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const add = async () => {
    const n = name.trim();
    const q = parseInt(qty, 10);
    if (!n) { toast.error("Type or pick a variety first."); return; }
    if (!Number.isFinite(q) || q < 0) { toast.error("Enter how many tubers you counted."); return; }
    setBusy(true);
    try {
      const match = catalog.find((v) => (v.name || "").toLowerCase() === n.toLowerCase());
      const r = await api.post("/admin/tuber-audit", { name: n, qty: q, variety_id: match?.id || null });
      toast.success(r.data?.merged ? `Added ${q} more ${r.data.name} — now ${r.data.qty} tubers.` : `${r.data.name} added with ${q} tubers.`);
      setName(""); setQty("");
      nameRef.current?.focus();
      await load();
      await reloadBoard();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add that."); }
    finally { setBusy(false); }
  };

  const setRowQty = async (row, newQty) => {
    const q = Math.max(0, newQty);
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, qty: q } : r)));
    try {
      await api.patch(`/admin/tuber-audit/${row.id}`, { qty: q });
      setTotals((t) => ({ ...t, total_tubers: t.total_tubers + (q - (row.qty || 0)) }));
      await reloadBoard();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update count."); await load(); }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Remove ${row.name} from the audit?`)) return;
    try {
      await api.delete(`/admin/tuber-audit/${row.id}`);
      toast.success(`${row.name} removed.`);
      await load();
      await reloadBoard();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove that."); }
  };

  const startFresh = async () => {
    if (!window.confirm(`Start a fresh audit? The current list (${totals.total_varieties} varieties, ${totals.total_tubers} tubers) is archived — nothing is deleted.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/tuber-audit/reset");
      toast.success(`Fresh audit started — ${r.data?.archived_rows || 0} rows archived.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reset."); }
    finally { setBusy(false); }
  };

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return needle ? rows.filter((r) => (r.name || "").toLowerCase().includes(needle)) : rows;
  }, [rows, filter]);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-tuber-audit">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.42em] uppercase font-semibold text-[#6E1E3A]">✦ Master stock list</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-1 text-[#3C2A1E]">Tuber Audit</h1>
          <p className="text-sm italic text-[#7A6A55] mt-1 max-w-2xl">Count what's actually in the shed. This list is your true availability — assign tubers to orders from here, subtract manually as you go.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => printAudit(rows, totals, board)} disabled={rows.length === 0} data-testid="tuber-audit-print"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#6E1E3A] text-white text-[11px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
            <Printer className="w-4 h-4" /> Print full audit
          </button>
          <button type="button" onClick={startFresh} disabled={busy || rows.length === 0} data-testid="tuber-audit-reset"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#A33030] text-[#A33030] bg-white text-[11px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
            <Archive className="w-4 h-4" /> Start fresh audit
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 max-w-md">
        <div className="bg-white border border-[#E8DFCB] rounded p-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">Varieties counted</div>
          <div className="font-serif text-3xl text-[#3C2A1E] mt-1" data-testid="tuber-audit-variety-total">{totals.total_varieties}</div>
        </div>
        <div className="bg-white border border-[#E8DFCB] rounded p-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">Tubers in stock</div>
          <div className="font-serif text-3xl text-[#6E1E3A] mt-1" data-testid="tuber-audit-tuber-total">{totals.total_tubers}</div>
        </div>
      </div>

      <AllocationSummary summary={board?.summary} />

      <AuditStatusPanel />
      <AuditSnapshots rowCount={rows.length} onChanged={async () => { await load(); await reloadBoard(); }} />

      <div className="bg-white border border-[#E8DFCB] rounded p-4">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] mb-2 inline-flex items-center gap-1.5"><Package className="w-4 h-4" /> Count a variety</div>
        <div className="flex flex-wrap gap-2">
          <input ref={nameRef} list="tuber-audit-catalog" value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Search catalogue or type a variety name…" data-testid="tuber-audit-name-input"
            className="flex-1 min-w-[240px] h-11 px-3 border border-[#E8DFCB] rounded text-sm bg-white outline-none focus:border-[#6E1E3A]" />
          <input type="number" min="0" value={qty} onChange={(e) => setQty(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Tubers" data-testid="tuber-audit-qty-input"
            className="w-28 h-11 px-3 border border-[#E8DFCB] rounded text-sm bg-white outline-none focus:border-[#6E1E3A]" />
          <button type="button" onClick={add} disabled={busy} data-testid="tuber-audit-add"
            className="h-11 px-5 bg-[#6E1E3A] hover:bg-[#5a1830] text-white rounded text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add to audit
          </button>
        </div>
        <p className="text-[11px] text-[#9C8B73] mt-2 italic">Adding a variety that's already on the list adds to its count — keep tallying as you go.</p>
        <datalist id="tuber-audit-catalog">{catalog.map((v) => <option key={v.id} value={v.name} />)}</datalist>
      </div>

      {loading ? (
        <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E8DFCB] p-10 text-center text-[#7A6A55] rounded">Nothing counted yet — start with your first variety above.</div>
      ) : (
        <div className="bg-white border border-[#E8DFCB] rounded">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E8DFCB]">
            <Search className="w-4 h-4 text-[#9C8B73]" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter the list…" data-testid="tuber-audit-filter"
              className="flex-1 outline-none text-sm bg-transparent" />
            <span className="text-[11px] text-[#9C8B73]">{visible.length} of {rows.length}</span>
          </div>
          <div className="divide-y divide-[#F0E8D8]">
            {visible.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`tuber-audit-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-[15px] text-[#3C2A1E] truncate">{r.name}</div>
                  {r.notes && <div className="text-[11px] italic text-[#9C8B73] truncate">{r.notes}</div>}
                  <Breakdown row={boardByKey[(r.name || "").trim().toLowerCase()]} onAllocate={() => setAllocating(boardByKey[(r.name || "").trim().toLowerCase()])} />
                </div>
                <button type="button" onClick={() => setRowQty(r, (r.qty || 0) - 1)} data-testid={`tuber-audit-minus-${r.id}`}
                  className="w-8 h-8 grid place-items-center border border-[#E8DFCB] rounded hover:border-[#6E1E3A] text-[#3C2A1E]"><Minus className="w-3.5 h-3.5" /></button>
                <input type="number" min="0" value={r.qty ?? 0}
                  onChange={(e) => setRowQty(r, parseInt(e.target.value || "0", 10))}
                  data-testid={`tuber-audit-rowqty-${r.id}`}
                  className="w-20 h-8 px-2 border border-[#E8DFCB] rounded text-sm text-center bg-white outline-none focus:border-[#6E1E3A]" />
                <button type="button" onClick={() => setRowQty(r, (r.qty || 0) + 1)} data-testid={`tuber-audit-plus-${r.id}`}
                  className="w-8 h-8 grid place-items-center border border-[#E8DFCB] rounded hover:border-[#6E1E3A] text-[#3C2A1E]"><Plus className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => removeRow(r)} data-testid={`tuber-audit-remove-${r.id}`}
                  className="w-8 h-8 grid place-items-center text-[#A33030] hover:bg-[#FBF0F0] rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {board && <GrowingFolders board={board} reload={reloadBoard} onPrint={(f) => printFolder(f)} />}
      {allocating && (
        <AllocateModal row={allocating} folders={board?.folders || []} onClose={() => setAllocating(null)}
          onDone={async () => { setAllocating(null); await reloadBoard(); }} />
      )}
    </div>
  );
}

function Breakdown({ row, onAllocate }) {
  if (!row) return null;
  const parts = [];
  if (row.orders) parts.push(`Customer orders ${row.orders}`);
  for (const g of row.growing_lines || []) parts.push(`${g.folder_name} ${g.qty}`);
  if (row.reserved) parts.push(`Held / sell list ${row.reserved}`);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]" data-testid={`tuber-audit-breakdown-${row.variety_key.replace(/[^a-z0-9]+/g, "-")}`}>
      {parts.map((p) => <span key={p} className="px-1.5 py-0.5 rounded bg-[#F3EBDD] text-[#6E1E3A]">{p}</span>)}
      <span className={`px-1.5 py-0.5 rounded font-bold ${row.available > 0 ? "bg-[#E4F0E8] text-[#2F6B4F]" : "bg-[#FBF0F0] text-[#A33030]"}`}>
        Available to sell {row.available}
      </span>
      {row.over_allocated > 0 && <span className="px-1.5 py-0.5 rounded bg-[#A33030] text-white font-bold">Over-allocated by {row.over_allocated}</span>}
      <button type="button" onClick={onAllocate} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#3E1F46] hover:underline"
        data-testid={`tuber-audit-allocate-${row.variety_key.replace(/[^a-z0-9]+/g, "-")}`}>Allocate to folder →</button>
    </div>
  );
}

const PRINT_CSS = `
    body{font-family:Georgia,'Times New Roman',serif;color:#3C2A1E;margin:28px;}
    h1{font-size:22px;margin:0;} .meta{font-size:12px;opacity:.7;margin:4px 0 16px;}
    table{border-collapse:collapse;width:100%;font-size:12px;}
    th{text-align:left;font-size:9px;letter-spacing:.2em;text-transform:uppercase;border-bottom:2px solid #C9A268;padding:6px 8px;color:#6E1E3A;}
    td{border-bottom:1px solid #E5D9C5;padding:6px 8px;}
    td.n{text-align:center;} td.n.total{font-weight:bold;font-size:14px;} td.n.avail{font-weight:bold;color:#2F6B4F;background:#F1F7F3;}
    td.n.zero{color:#A33030;}
    tfoot td{border-top:2px solid #C9A268;font-weight:bold;}
    .legend{font-size:11px;margin:10px 0 14px;padding:8px 10px;border:1px solid #C9A268;background:#FBF6E9;}
    @media print{.noprint{display:none;}}`;

function openPrint(title, body) {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><title>${title}</title><style>${PRINT_CSS}</style></head><body>
    <div class="noprint" style="text-align:right"><button onclick="window.print()" style="padding:8px 18px;cursor:pointer;">Print</button></div>${body}</body></html>`);
  w.document.close();
}

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function printAudit(rows, totals, board) {
  const folders = board?.folders || [];
  const byKey = {};
  for (const r of board?.rows || []) byKey[r.variety_key] = r;
  const sum = { final: 0, orders: 0, reserved: 0, available: 0, folders: Object.fromEntries(folders.map((f) => [f.id, 0])) };
  const trs = rows.map((r) => {
    const b = byKey[(r.name || "").trim().toLowerCase()] || {};
    const final = Number(r.qty || 0);
    const orders = Number(b.orders || 0), reserved = Number(b.reserved || 0), available = Number(b.available ?? final);
    sum.final += final; sum.orders += orders; sum.reserved += reserved; sum.available += available;
    const folderCells = folders.map((f) => {
      const g = (b.growing_lines || []).find((x) => x.folder_id === f.id);
      const q = g ? Number(g.qty) : 0;
      sum.folders[f.id] += q;
      return `<td class="n">${q || ""}</td>`;
    }).join("");
    return `<tr><td>${esc(r.name)}</td><td class="n total">${final}</td><td class="n">${orders || ""}</td>${folderCells}<td class="n">${reserved || ""}</td><td class="n avail${available ? "" : " zero"}">${available}</td></tr>`;
  }).join("");
  const folderHead = folders.map((f) => `<th style="text-align:center">${esc(f.name)}</th>`).join("");
  const folderFoot = folders.map((f) => `<td class="n">${sum.folders[f.id]}</td>`).join("");
  openPrint("Tuber audit", `
    <h1>Heritage Dahlias — Tuber Audit &amp; Stock Allocation</h1>
    <div class="meta">${totals.total_varieties} varieties · ${totals.total_tubers} tubers counted · ${new Date().toLocaleString("en-NZ")}</div>
    <div class="legend"><strong>Counted</strong> = total tubers on the audit. Each variety is then split into <strong>Customer orders</strong>, each <strong>growing folder</strong>, <strong>Held / sell list</strong>, and what is left as <strong>Available to sell</strong>. Counted always equals the row added together.</div>
    <table>
      <thead><tr><th>Variety</th><th style="text-align:center">Counted</th><th style="text-align:center">Customer orders</th>${folderHead}<th style="text-align:center">Held / sell list</th><th style="text-align:center">Available to sell</th></tr></thead>
      <tbody>${trs}</tbody>
      <tfoot><tr><td>Total</td><td class="n">${sum.final}</td><td class="n">${sum.orders}</td>${folderFoot}<td class="n">${sum.reserved}</td><td class="n avail">${sum.available}</td></tr></tfoot>
    </table>`);
}

function printFolder(folder) {
  const lines = (folder.lines || []).map((l) => `<tr><td>${esc(l.name)}</td><td class="n total">${l.qty}</td><td style="width:120px;border-left:1px dashed #C9A268"></td></tr>`).join("");
  openPrint(`Folder — ${folder.name}`, `
    <h1>${esc(folder.name)}</h1>
    <div class="meta">Growing / allocation folder · ${(folder.lines || []).length} varieties · ${folder.total || 0} tubers · ${new Date().toLocaleString("en-NZ")}${folder.notes ? ` · ${esc(folder.notes)}` : ""}</div>
    <table>
      <thead><tr><th>Variety</th><th style="text-align:center">Tubers</th><th>Planted / notes</th></tr></thead>
      <tbody>${lines || '<tr><td colspan="3" style="font-style:italic;opacity:.6">Nothing allocated to this folder yet.</td></tr>'}</tbody>
      <tfoot><tr><td>Total</td><td class="n">${folder.total || 0}</td><td></td></tr></tfoot>
    </table>`);
}
